<script lang="ts">
	import { OverlayScrollbars } from 'overlayscrollbars'

	export type ViewportScrollDetail = {
		scrollTop: number
		scrollHeight: number
		clientHeight: number
	}

	interface Props {
		class?: string
		initialScrollToBottom?: boolean
		trackScrollPosition?: boolean
		onViewportScroll?: ((detail: ViewportScrollDetail) => void) | null
		padContentForScrollbar?: boolean
		children?: import('svelte').Snippet | null
		containerRef?: HTMLDivElement | null // optional ref exposed to parent
	}

	let {
		class: className = '',
		initialScrollToBottom,
		children = null,
		trackScrollPosition = false,
		onViewportScroll = null,
		padContentForScrollbar = false,
		containerRef = $bindable(null) // bindable so parent can read internal div
	}: Props = $props()

	let osInstance: OverlayScrollbars | null
	let savedScrollTop = 0
	let scrollbarVisible = $state(false)

	$effect(() => {
		if (!containerRef) return

		osInstance = OverlayScrollbars(containerRef, {
			scrollbars: { autoHide: 'leave' }
		})

		const { viewport: vp, scrollbarHorizontal, scrollbarVertical } = osInstance.elements()

		// Remove scrollbar elements from tab order
		;[vp, scrollbarHorizontal.scrollbar, scrollbarVertical.scrollbar].forEach(
			(el) => el && (el.tabIndex = -1)
		)

		// Observe vertical scrollbar visibility to drive content padding
		let scrollbarObserver: MutationObserver | null = null
		if (padContentForScrollbar) {
			const vScrollbar = scrollbarVertical.scrollbar
			scrollbarObserver = new MutationObserver(() => {
				const isHidden = vScrollbar.classList.contains('os-scrollbar-auto-hide-hidden')
				const isUnusable = vScrollbar.classList.contains('os-scrollbar-unusable')
				scrollbarVisible = !isHidden && !isUnusable
			})
			scrollbarObserver.observe(vScrollbar, { attributes: true, attributeFilter: ['class'] })
		}

		// Add scroll tracking ONLY if enabled
		const onScroll = () => {
			if (trackScrollPosition) {
				savedScrollTop = vp.scrollTop
			}
			onViewportScroll?.({
				scrollTop: vp.scrollTop,
				scrollHeight: vp.scrollHeight,
				clientHeight: vp.clientHeight
			})
		}

		if (trackScrollPosition || onViewportScroll) {
			vp.addEventListener('scroll', onScroll)
		}

		// Initial behavior
		if (initialScrollToBottom) {
			vp.scrollTop = vp.scrollHeight
			savedScrollTop = vp.scrollTop
		}

		return () => {
			if (trackScrollPosition || onViewportScroll) {
				vp.removeEventListener('scroll', onScroll)
			}
			scrollbarObserver?.disconnect()
			osInstance?.destroy()
			osInstance = null
		}
	})

	export function scrollToBottom(behavior: ScrollBehavior = 'smooth') {
		osInstance?.elements().viewport.scrollTo({
			top: osInstance?.elements().viewport.scrollHeight || 0,
			behavior
		})
	}

	export function updateScrollbars(force: boolean = false) {
		osInstance?.update(force)
	}

	export function getSavedScrollTop() {
		return savedScrollTop
	}

	export function restoreScrollTop(behavior: ScrollBehavior = 'auto') {
		const vp = osInstance?.elements().viewport
		if (!vp) return
		vp.scrollTo({ top: savedScrollTop, behavior })
	}
</script>

<div bind:this={containerRef} class="{className} overlay-scrollbar">
	<div class="content-wrapper" class:scrollbar-visible={padContentForScrollbar && scrollbarVisible}>
		{@render children?.()}
	</div>
</div>

<style>
	.overlay-scrollbar {
		@apply h-full w-full;
	}

	.content-wrapper {
		height: 100%;
		transition: padding-right 150ms ease;
	}

	.content-wrapper.scrollbar-visible {
		padding-right: 10px;
	}
</style>
